import React, { useState } from 'react';
import { List } from '@refinedev/antd';
import { useList } from '@refinedev/core';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  message,
  Typography,
  Space,
} from 'antd';
import { GiftOutlined } from '@ant-design/icons';
import { UserDto } from '@wellverse/shared-ts';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';

const { Text, Paragraph } = Typography;

export const ItemGrantPage: React.FC = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const { data: usersData } = useList<UserDto>({
    resource: 'users',
    pagination: { pageSize: 200 },
  });

  const userOptions = (usersData?.data ?? []).map((u) => ({
    value: u.id,
    label: `${u.fullName || 'User'} (${u.email || u.phone || u.id})`,
  }));

  const submitGrant = async () => {
    const values = await form.validateFields();
    const freezeTokens = values.freezeTokens ?? 0;
    const recoveryDays = values.recoveryDays ?? 0;
    if (freezeTokens <= 0 && recoveryDays <= 0) {
      message.warning('Enter at least one freeze token or recovery day');
      return;
    }

    setSubmitting(true);
    try {
      await axiosInstance.post(`${API_BASE_URL}/admin/gamification/grants/items`, {
        userId: values.userId,
        freezeTokens: freezeTokens > 0 ? freezeTokens : undefined,
        recoveryDays: recoveryDays > 0 ? recoveryDays : undefined,
        description: values.description,
      });
      message.success('Items granted successfully');
      form.resetFields();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Grant failed';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <List title="Item Grants">
      <Card style={{ maxWidth: 560 }}>
        <Paragraph type="secondary">
          Manually grant streak freeze tokens or recovery days. Changes are logged
          server-side via inventory service.
        </Paragraph>
        <Form form={form} layout="vertical" onFinish={submitGrant}>
          <Form.Item
            name="userId"
            label="User"
            rules={[{ required: true, message: 'Select a user' }]}
          >
            <Select
              showSearch
              placeholder="Search user"
              options={userOptions}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Space size="large" style={{ width: '100%' }}>
            <Form.Item name="freezeTokens" label="Freeze tokens" initialValue={0}>
              <InputNumber min={0} max={99} />
            </Form.Item>
            <Form.Item name="recoveryDays" label="Recovery days" initialValue={0}>
              <InputNumber min={0} max={99} />
            </Form.Item>
          </Space>
          <Form.Item
            name="description"
            label="Reason"
            rules={[
              { required: true, message: 'Reason is required for audit' },
              { min: 3, message: 'At least 3 characters' },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="e.g. Customer support compensation for lost streak"
              maxLength={500}
              showCount
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<GiftOutlined />}
              loading={submitting}
            >
              Grant items
            </Button>
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Tip: use Coin Ledger for coin adjustments; this form is for freeze /
          recovery inventory only.
        </Text>
      </Card>
    </List>
  );
};
